// TODO: replace with live values once the ASN Gap Location Summary API is wired up.
// Deterministic lane-by-week fact model ported from the ASN Gap Navigator mock,
// so region/market/location rollups always tie back to the source Score Card.
import { AsnGapPerspective } from './components/LocationSummary';

export type LocationType = 'Plant' | 'Distribution Center' | 'Own Warehouse' | 'Repack' | 'CoMan' | 'Third Party';

interface LocationMaster {
  n: string; // name
  r: string; // region / unit
  m: string; // market / POD
  t: LocationType;
  wh: string; // warehouse system
}

interface Lane {
  i: number;
  s: number; // shipper index into LOCS
  r: number; // receiver index into LOCS
  w: number; // relative weight
  nr: number; // non-ASN propensity multiplier
  casePer: number;
  skuPer: number;
  py: number; // prior-year % non-ASN
}

export interface Fact {
  L: number; // lane index
  w: number; // week slot index
  loads: number;
  ny: number; // non-ASN loads
}

export interface Rollup {
  loads: number;
  ny: number;
  cases: number;
  nyCases: number;
  skus: number;
  nySkus: number;
  pct: number;
  py: number;
}

export type GrainKey =
  | 'region'
  | 'market'
  | 'location'
  | 'shipLoc'
  | 'recvLoc'
  | 'locType'
  | 'locationType'
  | 'shipType'
  | 'whSystem'
  | 'year'
  | 'period'
  | 'week';

export const TARGET_NONASN = 25.0;
export const overTarget = (v: number): boolean => v > TARGET_NONASN;

const T_PLANT: LocationType = 'Plant';
const T_DC: LocationType = 'Distribution Center';
const T_OW: LocationType = 'Own Warehouse';
const T_RP: LocationType = 'Repack';
const T_CM: LocationType = 'CoMan';
const T_3P: LocationType = 'Third Party';

const LOCS: LocationMaster[] = [
  // Northeast
  { n: 'US-PL-WYTHEVILLE-VA-CBM', r: 'Northeast', m: 'Mid-Atlantic', t: T_PLANT, wh: '' },
  { n: 'US-DC-RICHMOND-VA-SC', r: 'Northeast', m: 'Mid-Atlantic', t: T_DC, wh: '' },
  { n: 'US-DC-CRANBURY-NJ-SC', r: 'Northeast', m: 'Metro New York', t: T_DC, wh: '' },
  { n: 'US-DC-BRONX-NY-SC', r: 'Northeast', m: 'Metro New York', t: T_DC, wh: '' },
  { n: 'US-DC-CRANSTON-RI-SC', r: 'Northeast', m: 'New England', t: T_DC, wh: '' },
  // Northcentral
  { n: 'US-DC-SAINT LOUIS-MO-SC', r: 'Northcentral', m: 'CHI / STL', t: T_DC, wh: '' },
  { n: 'US-DC-CHICAGO-IL-SC', r: 'Northcentral', m: 'CHI / STL', t: T_DC, wh: '' },
  { n: 'US-DC-EAGAN-MN-SC', r: 'Northcentral', m: 'Upper Midwest', t: T_DC, wh: '' },
  { n: 'US-PL-ROCKFORD-IL-CBM', r: 'Northcentral', m: 'Upper Midwest', t: T_PLANT, wh: '' },
  // Atlantic
  { n: 'US-DC-KIMBALL-MI-SC', r: 'Atlantic', m: 'Michigan', t: T_DC, wh: '' },
  { n: 'US-DC-DETROIT-MI-SC', r: 'Atlantic', m: 'Michigan', t: T_DC, wh: '' },
  { n: 'US-DC-COLUMBUS-OH-SC', r: 'Atlantic', m: 'Ohio Valley', t: T_DC, wh: '' },
  { n: 'US-DC-PHILADELPHIA-PA-SC', r: 'Atlantic', m: 'Keystone', t: T_DC, wh: '' },
  { n: 'US-DC-BALTIMORE-MD-SC', r: 'Atlantic', m: 'Keystone', t: T_DC, wh: '' },
  // Southeast
  { n: 'US-PL-STONE MOUNTAIN-GA-CBM', r: 'Southeast', m: 'Georgia', t: T_PLANT, wh: '' },
  { n: 'US-DC-COMMERCE-GA-SC', r: 'Southeast', m: 'Georgia', t: T_DC, wh: '' },
  { n: 'US-DC-LITHONIA-GA-SC', r: 'Southeast', m: 'Georgia', t: T_DC, wh: '' },
  { n: 'US-DC-ORLANDO-FL-SC', r: 'Southeast', m: 'Florida', t: T_DC, wh: '' },
  { n: 'US-DC-TAMPA-FL-SC', r: 'Southeast', m: 'Florida', t: T_DC, wh: '' },
  { n: 'US-DC-COLUMBIA-SC-SC', r: 'Southeast', m: 'Carolinas', t: T_DC, wh: '' },
  { n: 'US-DC-CHARLOTTE-NC-SC', r: 'Southeast', m: 'Carolinas', t: T_DC, wh: '' },
  // Texoma
  { n: 'US-PL-TULSA-OK-CBM', r: 'Texoma', m: 'Oklahoma', t: T_PLANT, wh: '' },
  { n: 'US-OW-TULSA-OK-CBM', r: 'Texoma', m: 'Oklahoma', t: T_OW, wh: '' },
  { n: 'US-PL-MESQUITE-TX-CBM', r: 'Texoma', m: 'North Texas', t: T_PLANT, wh: '' },
  { n: 'US-DC-FORT WORTH-TX-SC', r: 'Texoma', m: 'North Texas', t: T_DC, wh: '' },
  { n: 'US-DC-ABILENE-TX-SC', r: 'Texoma', m: 'North Texas', t: T_DC, wh: '' },
  // West
  { n: 'US-DC-RIDGEFIELD-WA-SC', r: 'West', m: 'Pacific Northwest', t: T_DC, wh: '' },
  { n: 'US-DC-AUBURN-WA-SC', r: 'West', m: 'Pacific Northwest', t: T_DC, wh: '' },
  { n: 'US-DC-CLACKAMAS-OR-SC', r: 'West', m: 'Pacific Northwest', t: T_DC, wh: '' },
  { n: 'US-DC-BUENA PARK-CA-SC', r: 'West', m: 'LA / S Cal', t: T_DC, wh: '' },
  { n: 'US-DC-CARSON-CA-SC', r: 'West', m: 'LA / S Cal', t: T_DC, wh: '' },
  { n: 'US-DC-ELKO-NV-SC', r: 'West', m: 'S.West / Hawaii', t: T_DC, wh: '' },
  { n: 'US-DC-MARYSVILLE-WA-SC', r: 'West', m: 'COE West', t: T_DC, wh: '' },
  // Canada
  { n: 'CA-PL-MISSISSAUGA-ON-PBCCAN', r: 'Canada', m: 'Ontario', t: T_PLANT, wh: '' },
  { n: 'CA-DC-MILTON-ON-PBGCAN', r: 'Canada', m: 'Ontario', t: T_DC, wh: '' },
  { n: 'CA-DC-OTTAWA-ON-PBCCAN', r: 'Canada', m: 'Ontario', t: T_DC, wh: '' },
  { n: 'CA-PL-CALGARY-AB-PBCCAN', r: 'Canada', m: 'Western Canada', t: T_PLANT, wh: '' },
  { n: 'CA-DC-EDMONTON-AB-PBCCAN', r: 'Canada', m: 'Western Canada', t: T_DC, wh: '' },
  { n: 'CA-DC-KELOWNA-BC-PBCCAN', r: 'Canada', m: 'Western Canada', t: T_DC, wh: '' },
  { n: 'CA-PL-DELTA-BC-PBCCAN', r: 'Canada', m: 'Western Canada', t: T_PLANT, wh: '' },
  { n: 'CA-DC-DELTA-BC-PBCCAN', r: 'Canada', m: 'Western Canada', t: T_DC, wh: '' },
  { n: 'CA-DC-NANAIMO-BC-PBCCAN', r: 'Canada', m: 'Western Canada', t: T_DC, wh: '' },
  // Unit 1
  { n: 'US-PL-RIVERSIDE-CA-CBM', r: 'Unit 1', m: 'Unit 1 West', t: T_PLANT, wh: '' },
  { n: 'US-OW-RIVERSIDE-CA-CBM', r: 'Unit 1', m: 'Unit 1 West', t: T_OW, wh: '' },
  { n: 'US-PL-LAS VEGAS-NV-CBM', r: 'Unit 1', m: 'Unit 1 West', t: T_PLANT, wh: '' },
  { n: 'US-PL-DENVER-CO-CBM', r: 'Unit 1', m: 'High Point', t: T_PLANT, wh: '' },
  // Unit 2
  { n: 'US-PL-SAINT LOUIS-MO-CBM', r: 'Unit 2', m: 'Central', t: T_PLANT, wh: '' },
  { n: 'US-PL-URBANDALE-IA-CBM', r: 'Unit 2', m: 'Central', t: T_PLANT, wh: '' },
  { n: 'US-OW-URBANDALE-IA-CBM-121 ST', r: 'Unit 2', m: 'Central', t: T_OW, wh: '' },
  { n: 'US-PL-WICHITA-KS-CBM', r: 'Unit 2', m: 'Central', t: T_PLANT, wh: '' },
  { n: 'US-OW-PARK CITY-KS-CBM', r: 'Unit 2', m: 'Central', t: T_OW, wh: '' },
  { n: 'US-PL-MUNSTER-IN-CBM', r: 'Unit 2', m: 'Central', t: T_PLANT, wh: '' },
  { n: 'US-DC-MUNSTER-IN-SC', r: 'Unit 2', m: 'Central', t: T_DC, wh: '' },
  { n: 'US-PL-JACKSONVILLE-FL-CBM', r: 'Unit 2', m: 'Florida Georgia', t: T_PLANT, wh: '' },
  // Unit 3
  { n: 'US-PL-LIMA-OH-CBM', r: 'Unit 3', m: 'Great Lakes', t: T_PLANT, wh: '' },
  { n: 'US-OW-LIMA-OH-CBM', r: 'Unit 3', m: 'Great Lakes', t: T_OW, wh: '' },
  { n: 'US-PL-TOLEDO-OH-CBM', r: 'Unit 3', m: 'Great Lakes', t: T_PLANT, wh: '' },
  { n: 'US-OW-TOLEDO-OH-CBM', r: 'Unit 3', m: 'Great Lakes', t: T_OW, wh: '' },
  { n: 'US-PL-INDIANAPOLIS-IN-CBM', r: 'Unit 3', m: 'Great Lakes', t: T_PLANT, wh: '' },
  { n: 'US-DC-INDIANAPOLIS-IN-SC', r: 'Unit 3', m: 'Great Lakes', t: T_DC, wh: '' },
  { n: 'US-PL-AUSTIN C/C-IN-CBM', r: 'Unit 3', m: 'Great Lakes', t: T_PLANT, wh: '' },
  { n: 'US-PL-JOHNSTOWN-PA-CBM-LAUREL', r: 'Unit 3', m: 'Allegheny', t: T_PLANT, wh: '' },
  { n: 'US-OW-JOHNSTOWN-PA-CBM', r: 'Unit 3', m: 'Allegheny', t: T_OW, wh: '' },
  // External: New_Shipping_Division = "External"
  { n: 'CA-RP-CALEDON-ON-PBCCAN', r: 'External', m: 'External Canada', t: T_RP, wh: '' },
  { n: 'CA-RP-DELTA-BC-VALHALLA', r: 'External', m: 'External Canada', t: T_RP, wh: '' },
  { n: 'CA-RP-MILTON-ON-PBGCAN', r: 'External', m: 'External Canada', t: T_RP, wh: '' },
  { n: 'US-ZD-EDGERTON-KS-CBM', r: 'External', m: 'External US', t: T_CM, wh: '' },
  { n: 'US-ZD-TACOMA-WA-ICC', r: 'External', m: 'External US', t: T_CM, wh: '' },
  { n: 'NNIS 224 DENVER CO', r: 'External', m: 'External US', t: T_CM, wh: '' },
  { n: 'US-ZD-BURLINGTON-NJ-3PO', r: 'External', m: 'External US', t: T_3P, wh: '' },
  { n: 'US-ZD-MCDONOUGH-GA-3PO', r: 'External', m: 'External US', t: T_3P, wh: '' },
];

export const REGION_ORDER = [
  'Northeast',
  'Northcentral',
  'Atlantic',
  'Southeast',
  'Texoma',
  'West',
  'Canada',
  'Unit 1',
  'Unit 2',
  'Unit 3',
  'External',
];

const REGION_TARGETS: Record<string, { ny: number; tot: number; py: number }> = {
  Northeast: { ny: 3711, tot: 11576, py: 33.72 },
  Northcentral: { ny: 2909, tot: 7847, py: 48.8 },
  Atlantic: { ny: 5028, tot: 17774, py: 28.17 },
  Southeast: { ny: 4309, tot: 14029, py: 36.92 },
  Texoma: { ny: 5500, tot: 9451, py: 58.96 },
  West: { ny: 2524, tot: 15271, py: 33.07 },
  Canada: { ny: 2396, tot: 14752, py: 37.07 },
  'Unit 1': { ny: 1781, tot: 10411, py: 25.45 },
  'Unit 2': { ny: 3692, tot: 12818, py: 42.82 },
  'Unit 3': { ny: 4953, tot: 10243, py: 48.71 },
  External: { ny: 7002, tot: 11580, py: 56.03 },
};

// Shippers with no ASN capability at all: always 100% non-ASN.
const NO_ASN_SITES = new Set([
  'CA-RP-CALEDON-ON-PBCCAN',
  'CA-RP-DELTA-BC-VALHALLA',
  'CA-RP-MILTON-ON-PBGCAN',
  'NNIS 224 DENVER CO',
]);

// Full-ASN-capability shippers that run clean: always 0% non-ASN.
const FULL_ASN_SITES = new Set([
  'US-DC-LITHONIA-GA-SC',
  'US-DC-COMMERCE-GA-SC',
  'US-DC-CLACKAMAS-OR-SC',
  'US-DC-EAGAN-MN-SC',
  'US-DC-CRANSTON-RI-SC',
  'CA-DC-NANAIMO-BC-PBCCAN',
]);

export const PERIODS = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6'];
export const WEEKS = ['W1', 'W2', 'W3', 'W4'];
const N_SLOTS = PERIODS.length * WEEKS.length;

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h;
}

function mulberry32(seed: number): () => number {
  return function next() {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Largest-remainder allocation: children always sum exactly to the parent.
function distribute(total: number, weights: number[]): number[] {
  const W = weights.reduce((a, b) => a + b, 0);
  if (W <= 0 || total <= 0) return weights.map(() => 0);
  const base: number[] = [];
  const fr: { i: number; f: number }[] = [];
  let used = 0;
  weights.forEach((w, i) => {
    const raw = (total * w) / W;
    const b = Math.floor(raw);
    base.push(b);
    fr.push({ i, f: raw - b });
    used += b;
  });
  fr.sort((a, b) => b.f - a.f);
  for (let k = 0; k < total - used; k++) {
    base[fr[k % fr.length].i]++;
  }
  return base;
}

LOCS.forEach((l) => {
  l.wh = ['WES', 'SAP EWM', 'Legacy WMS'][hashStr(l.n) % 3];
});

const INTERNAL = LOCS.filter((l) => l.r !== 'External');
const RECEIVERS_POOL = INTERNAL.filter((l) => l.t === T_DC || l.t === T_OW);

const LANES: Lane[] = [];
LOCS.forEach((sl, slIdx) => {
  const rnd = mulberry32(hashStr('lane|' + sl.n));
  const local = RECEIVERS_POOL.filter((r) => r.n !== sl.n && r.m === sl.m);
  const regional = RECEIVERS_POOL.filter((r) => r.n !== sl.n && r.r === sl.r && r.m !== sl.m);
  const far = RECEIVERS_POOL.filter((r) => r.r !== sl.r);
  const picks: LocationMaster[] = [];
  const take = (pool: LocationMaster[], k: number) => {
    const c = pool.slice();
    for (let j = 0; j < k && c.length; j++) {
      picks.push(c.splice(Math.floor(rnd() * c.length), 1)[0]);
    }
  };
  take(local, local.length ? 2 : 0);
  take(regional, 1);
  take(far, sl.r === 'External' ? 2 : 1);
  if (!picks.length) take(far, 2);
  const seen: Record<string, boolean> = {};
  picks
    .filter((p) => p && !seen[p.n] && (seen[p.n] = true))
    .forEach((rl, k) => {
      const h = hashStr(sl.n + '>' + rl.n);
      const sameSite = sl.n.split('-').slice(2, 3).join('') === rl.n.split('-').slice(2, 3).join('');
      LANES.push({
        i: LANES.length,
        s: slIdx,
        r: LOCS.indexOf(rl),
        w: (sameSite ? 6 : k === 0 ? 4 : 2) + (h % 5),
        nr: sameSite ? 1.9 : 0.5 + (h % 7) / 7,
        casePer: 780 + (h % 2500),
        skuPer: 14 + (h % 34),
        py: Math.max(0.4, Math.min(99.6, REGION_TARGETS[sl.r].py + ((h % 1400) / 100 - 7))),
      });
    });
});

// Allocate each shipping region's margins across its lane-by-week cells so
// every rollup ties exactly to the source Score Card.
const FACTS: Fact[] = [];
(function buildFacts() {
  REGION_ORDER.forEach((rg) => {
    const tgt = REGION_TARGETS[rg];
    const lanes = LANES.filter((L) => LOCS[L.s].r === rg);
    if (!lanes.length) return;
    const cells: { L: Lane; w: number; wt: number }[] = [];
    lanes.forEach((L) => {
      const rnd = mulberry32(hashStr('slot|' + L.i));
      for (let w = 0; w < N_SLOTS; w++) cells.push({ L, w, wt: L.w * (0.62 + rnd() * 0.76) });
    });
    const loads = distribute(
      tgt.tot,
      cells.map((c) => c.wt)
    );
    const forcedIdx: number[] = [];
    const zeroIdx: number[] = [];
    const freeIdx: number[] = [];
    cells.forEach((c, i) => {
      const n = LOCS[c.L.s].n;
      if (NO_ASN_SITES.has(n)) forcedIdx.push(i);
      else if (FULL_ASN_SITES.has(n)) zeroIdx.push(i);
      else freeIdx.push(i);
    });
    const forcedTotal = forcedIdx.reduce((a, i) => a + loads[i], 0);
    const freeCap = freeIdx.reduce((a, i) => a + loads[i], 0);
    const remainder = tgt.ny - forcedTotal;
    let ny: number[];
    if ((forcedIdx.length || zeroIdx.length) && remainder >= 0 && remainder <= freeCap) {
      ny = loads.map(() => 0);
      forcedIdx.forEach((i) => {
        ny[i] = loads[i];
      });
      const rest = distribute(
        remainder,
        freeIdx.map((i) => Math.max(0.001, loads[i] * cells[i].L.nr))
      );
      freeIdx.forEach((i, k) => {
        ny[i] = rest[k];
      });
    } else {
      ny = distribute(
        tgt.ny,
        cells.map((c, i) => Math.max(0.001, loads[i] * c.L.nr))
      );
    }
    for (let pass = 0; pass < 8; pass++) {
      let over = 0;
      ny.forEach((v, i) => {
        if (v > loads[i]) {
          over += v - loads[i];
          ny[i] = loads[i];
        }
      });
      if (!over) break;
      const room = loads.map((v, i) => v - ny[i]);
      const totRoom = room.reduce((a, b) => a + b, 0);
      if (totRoom <= 0) break;
      distribute(Math.min(over, totRoom), room).forEach((v, i) => {
        ny[i] += v;
      });
    }
    cells.forEach((c, i) => {
      if (loads[i] > 0) FACTS.push({ L: c.L.i, w: c.w, loads: loads[i], ny: ny[i] });
    });
  });
})();

export const shipOf = (f: Fact): LocationMaster => LOCS[LANES[f.L].s];
export const recvOf = (f: Fact): LocationMaster => LOCS[LANES[f.L].r];
const casesOf = (f: Fact): number => f.loads * LANES[f.L].casePer;
const nyCasesOf = (f: Fact): number => f.ny * LANES[f.L].casePer;
const skusOf = (f: Fact): number => f.loads * LANES[f.L].skuPer;
const nySkusOf = (f: Fact): number => f.ny * LANES[f.L].skuPer;

const sideLoc = (f: Fact, persp: AsnGapPerspective): LocationMaster =>
  persp === 'receiving' ? recvOf(f) : shipOf(f);

const shipTypeOf = (f: Fact): string =>
  shipOf(f).r === 'External' ? 'External Shipments (PO)' : 'Internal Shipments (BOL)';

export function rollup(list: Fact[]): Rollup {
  let loads = 0;
  let ny = 0;
  let cases = 0;
  let nyCases = 0;
  let skus = 0;
  let nySkus = 0;
  let pyN = 0;
  for (const f of list) {
    loads += f.loads;
    ny += f.ny;
    cases += casesOf(f);
    nyCases += nyCasesOf(f);
    skus += skusOf(f);
    nySkus += nySkusOf(f);
    pyN += LANES[f.L].py * f.loads;
  }
  return {
    loads,
    ny,
    cases,
    nyCases,
    skus,
    nySkus,
    pct: loads ? (ny / loads) * 100 : 0,
    py: loads ? pyN / loads : 0,
  };
}

export function groupBy<T>(list: Fact[], keyFn: (f: Fact) => T): Map<T, Fact[]> {
  const m = new Map<T, Fact[]>();
  for (const f of list) {
    const k = keyFn(f);
    if (k === null || k === undefined) continue;
    let a = m.get(k);
    if (!a) {
      a = [];
      m.set(k, a);
    }
    a.push(f);
  }
  return m;
}

const slotMeta = (i: number): { year: string; period: string; week: string } => ({
  year: '2026',
  period: PERIODS[Math.floor(i / 4)],
  week: WEEKS[i % 4],
});

interface GrainDef {
  key: GrainKey;
  label: string;
  val: (f: Fact, persp: AsnGapPerspective) => string;
}

export const GRAINS: GrainDef[] = [
  { key: 'region', label: 'Region / Unit', val: (f, persp) => sideLoc(f, persp).r },
  { key: 'market', label: 'Market / POD', val: (f, persp) => sideLoc(f, persp).m },
  { key: 'location', label: 'Location', val: (f, persp) => sideLoc(f, persp).n },
  { key: 'shipLoc', label: 'Shipping Location', val: (f) => shipOf(f).n },
  { key: 'recvLoc', label: 'Receiving Location', val: (f) => recvOf(f).n },
  { key: 'locType', label: 'Location Type', val: (f, persp) => sideLoc(f, persp).t },
  // 'locationType' is the key name returned by the API — aliased here so
  // grainLabel resolves correctly when the API response uses this spelling.
  { key: 'locationType', label: 'Location Type', val: (f, persp) => sideLoc(f, persp).t },
  { key: 'shipType', label: 'Shipment Type', val: (f) => shipTypeOf(f) },
  { key: 'whSystem', label: 'Warehouse System', val: (f, persp) => sideLoc(f, persp).wh },
  { key: 'year', label: 'Year', val: (f) => slotMeta(f.w).year },
  { key: 'period', label: 'Period', val: (f) => slotMeta(f.w).period },
  { key: 'week', label: 'Week', val: (f) => slotMeta(f.w).week },
];

export const GR = (key: GrainKey): GrainDef => {
  const g = GRAINS.find((x) => x.key === key);
  if (!g) throw new Error(`Unknown grain key: ${key}`);
  return g;
};

export const grainLabel = (key: GrainKey): string => GR(key).label;

// Full generated dataset for the perspective; no external filter-bar wiring is in scope.
export function scopedFacts(_persp: AsnGapPerspective): Fact[] {
  return FACTS;
}

// With both location columns selected, the perspective side leads so the merged
// parent cell reads one-to-many on shipping and many-to-one on receiving.
export function effectiveKeys(selectedKeys: GrainKey[], persp: AsnGapPerspective): GrainKey[] {
  const keys = selectedKeys.filter(Boolean);
  const hasShip = keys.includes('shipLoc');
  const hasRecv = keys.includes('recvLoc');
  if (!(hasShip && hasRecv)) return keys;
  const lead: GrainKey = persp === 'receiving' ? 'recvLoc' : 'shipLoc';
  const trail: GrainKey = persp === 'receiving' ? 'shipLoc' : 'recvLoc';
  const out: GrainKey[] = [];
  let placed = false;
  keys.forEach((k) => {
    if (k === 'shipLoc' || k === 'recvLoc') {
      if (!placed) {
        out.push(lead, trail);
        placed = true;
      }
    } else {
      out.push(k);
    }
  });
  return out;
}
